/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { riskPresetsSuggest } from "../funcs/riskPresetsSuggest.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { SuggestRiskPolicyResult } from "../models/components/suggestriskpolicyresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SuggestRiskPolicyRequest,
  SuggestRiskPolicySecurity,
} from "../models/operations/suggestriskpolicy.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RiskSuggestPolicyMutationVariables = {
  request: SuggestRiskPolicyRequest;
  security?: SuggestRiskPolicySecurity | undefined;
  options?: RequestOptions;
};

export type RiskSuggestPolicyMutationData = SuggestRiskPolicyResult;

export type RiskSuggestPolicyMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * suggestRiskPolicy risk
 *
 * @remarks
 * Map a plain-language description of a risk to a policy draft: the closest preset with its detectors, action and severity, or a bespoke prompt-based guardrail when no preset fits. Uses the configured LLM when available and a deterministic keyword match otherwise. Nothing is created.
 */
export function useRiskSuggestPolicyMutation(
  options?: MutationHookOptions<
    RiskSuggestPolicyMutationData,
    RiskSuggestPolicyMutationError,
    RiskSuggestPolicyMutationVariables
  >,
): UseMutationResult<
  RiskSuggestPolicyMutationData,
  RiskSuggestPolicyMutationError,
  RiskSuggestPolicyMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRiskSuggestPolicyMutation(client, options),
    ...options,
  });
}

export function mutationKeyRiskSuggestPolicy(): MutationKey {
  return ["@gram/client", "presets", "suggest"];
}

export function buildRiskSuggestPolicyMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RiskSuggestPolicyMutationVariables,
  ) => Promise<RiskSuggestPolicyMutationData>;
} {
  return {
    mutationKey: mutationKeyRiskSuggestPolicy(),
    mutationFn: function riskSuggestPolicyMutationFn({
      request,
      security,
      options,
    }): Promise<RiskSuggestPolicyMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(riskPresetsSuggest(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
